import React from 'react';
import { useTranslation } from 'react-i18next';

function TagInfoPage({ onClose }) {
  const { t } = useTranslation('dashboard');
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70" onClick={onClose}></div>

      {/* Content */}
      <div className="relative min-h-screen flex items-start justify-center p-4 pt-20">
        <div className="relative bg-anthracite-900 border border-anthracite-700 rounded-2xl max-w-3xl w-full shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-anthracite-700">
            <h2 className="text-xl font-bold text-white">{t('predictionTagsExplained')}</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-anthracite-800 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <p className="text-gray-400 text-sm">
              {t('tagIntro')}
            </p>

            <div className="space-y-5">
              <TagExplanation
                tag="ML"
                title={t('tagML')}
                color="blue"
                description={t('tagMLDesc')}
              />

              <TagExplanation
                tag="H2H"
                title={t('tagH2H')}
                color="purple"
                description={t('tagH2HDesc')}
              />

              <TagExplanation
                tag="GPG"
                title={t('tagGPG')}
                color="orange"
                description={t('tagGPGDesc')}
              />

              <TagExplanation
                tag="VETO"
                title={t('tagVETO')}
                color="green"
                description={t('tagVETODesc')}
              />

              <TagExplanation
                tag="FAV"
                title={t('tagFAV')}
                color="purple"
                description={t('tagFAVDesc')}
              />

              <TagExplanation
                tag="HOME"
                title={t('tagHOME')}
                color="orange"
                description={t('tagHOMEDesc')}
              />

              <TagExplanation
                tag="UPSET"
                title={t('tagUPSET')}
                color="red"
                description={t('tagUPSETDesc')}
              />

              <TagExplanation
                tag="FORM"
                title={t('tagFORM')}
                color="teal"
                description={t('tagFORMDesc')}
              />
            </div>

            {/* Draw Tags */}
            <div className="pt-4 border-t border-anthracite-700">
              <h3 className="text-sm font-semibold text-gray-400 mb-4">{t('drawPredictionTags')}</h3>
              <div className="space-y-5">
                <TagExplanation
                  tag="BAL"
                  title={t('tagBAL')}
                  color="cyan"
                  description={t('tagBALDesc')}
                />

                <TagExplanation
                  tag="CLOSE"
                  title={t('tagCLOSE')}
                  color="indigo"
                  description={t('tagCLOSEDesc')}
                />

                <TagExplanation
                  tag="DEF"
                  title={t('tagDEF')}
                  color="slate"
                  description={t('tagDEFDesc')}
                />

                <TagExplanation
                  tag="PROF"
                  title={t('tagPROF')}
                  color="amber"
                  description={t('tagPROFDesc')}
                />

                <TagExplanation
                  tag="CONV"
                  title={t('tagCONV')}
                  color="emerald"
                  description={t('tagCONVDesc')}
                />
              </div>
            </div>


            {/* Over 2.5 Tags */}
            <div className="pt-4 border-t border-anthracite-700">
              <h3 className="text-sm font-semibold text-gray-400 mb-4">{t('over25Tags')}</h3>
              <div className="space-y-5">
                <TagExplanation tag="O25" title={t('tagO25')} color="blue" description={t('tagO25Desc')} />
              </div>
            </div>

            {/* Ultimate Tags */}
            <div className="pt-4 border-t border-anthracite-700">
              <h3 className="text-sm font-semibold text-gray-400 mb-4">{t('ultimateTags')}</h3>
              <div className="space-y-5">
                <TagExplanation tag="DRAW+CS" title={t('tagDRAWCS')} color="emerald" description={t('tagDRAWCSDesc')} />
                <TagExplanation tag="CS+" title={t('tagCSPlus')} color="amber" description={t('tagCSPlusDesc')} />
                <TagExplanation tag="DRAW+" title={t('tagDRAWPlus')} color="cyan" description={t('tagDRAWPlusDesc')} />
                <TagExplanation tag="3ML" title={t('tag3ML')} color="purple" description={t('tag3MLDesc')} />
                <TagExplanation tag="CLEAN" title={t('tagCLEAN')} color="green" description={t('tagCLEANDesc')} />
                <TagExplanation tag="XVAL" title={t('tagXVAL')} color="lime" description={t('tagXVALDesc')} />
              </div>
            </div>

            {/* Footer note */}
            <div className="pt-4 border-t border-anthracite-700">
              <p className="text-xs text-gray-500">
                {t('tagNote')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TagExplanation({ tag, title, color, description }) {
  const colorStyles = {
    blue: 'bg-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/20 text-purple-400',
    orange: 'bg-orange-500/20 text-orange-400',
    green: 'bg-green-500/20 text-green-400',
    red: 'bg-red-500/20 text-red-400',
    teal: 'bg-teal-500/20 text-teal-400',
    cyan: 'bg-cyan-500/20 text-cyan-400',
    indigo: 'bg-indigo-500/20 text-indigo-400',
    slate: 'bg-slate-500/20 text-slate-400',
    amber: 'bg-amber-500/20 text-amber-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
  };

  return (
    <div className="bg-anthracite-800/50 rounded-xl p-4 border border-anthracite-700">
      <div className="flex items-center gap-3 mb-2">
        <span className={`px-2 py-1 rounded text-xs font-bold ${colorStyles[color]}`}>
          {tag}
        </span>
        <h3 className="text-white font-medium">{title}</h3>
      </div>
      <p className="text-gray-400 text-sm leading-relaxed">
        {description}
      </p>
    </div>
  );
}

export default TagInfoPage;
